/**
 * Admin i18n — Painel de gerenciamento de idiomas e traduções
 * Abas: Idiomas, Traduções, Auto-Tradução, Configurações
 */

import { useState, useEffect, useMemo, useCallback } from 'preact/hooks';
import { api } from '../api/client';
import { T } from '../styles/tokens';
import { SUPPORTED_LANGUAGES, type Language } from '../locales/languages';

// ── Estilos ──
const s = {
  page: { padding: '24px 32px', maxWidth: 1400, margin: '0 auto' } as any,
  title: { fontSize: 24, fontWeight: 700, color: T.text, marginBottom: 8 } as any,
  subtitle: { fontSize: 14, color: T.textMuted, marginBottom: 24 } as any,
  tabBar: { display: 'flex', gap: 0, borderBottom: `1px solid ${T.border}`, marginBottom: 24, overflowX: 'auto' as any } as any,
  tab: (active: boolean) => ({
    padding: '10px 20px', cursor: 'pointer', fontSize: 13, fontWeight: active ? 600 : 500,
    color: active ? T.primary : T.textSecondary,
    borderBottom: active ? `2px solid ${T.primary}` : '2px solid transparent',
    whiteSpace: 'nowrap' as any, transition: 'all 0.15s', background: 'none', border: 'none',
    borderBottomWidth: 2, borderBottomStyle: 'solid' as any,
    borderBottomColor: active ? T.primary : 'transparent',
  }),
  card: { background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12, padding: 16, transition: 'all 0.15s' } as any,
  cardHover: { boxShadow: `0 0 0 1px ${T.primary}` } as any,
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 } as any,
  searchRow: { display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' as any } as any,
  input: { padding: '8px 14px', borderRadius: 8, fontSize: 14, background: T.bgInput, border: `1px solid ${T.border}`, color: T.text, outline: 'none', flex: 1, minWidth: 200 } as any,
  select: { padding: '8px 14px', borderRadius: 8, fontSize: 14, background: T.bgInput, border: `1px solid ${T.border}`, color: T.text, outline: 'none' } as any,
  btn: (variant: 'primary' | 'danger' | 'ghost' | 'success') => ({
    padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none',
    display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'all 0.15s',
    ...(variant === 'primary' ? { background: T.primary, color: '#fff' } :
       variant === 'danger' ? { background: T.danger, color: '#fff' } :
       variant === 'success' ? { background: T.success, color: '#fff' } :
       { background: T.border, color: T.textSecondary }),
  }),
  badge: (pct: number) => ({
    fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
    color: '#fff',
    background: pct >= 80 ? T.success : pct >= 50 ? T.warning : T.danger,
  }),
  toggle: (on: boolean) => ({
    width: 40, height: 22, borderRadius: 11, cursor: 'pointer', border: 'none',
    background: on ? T.primary : T.border, position: 'relative' as any,
    transition: 'background 0.2s',
  }),
  toggleDot: (on: boolean) => ({
    width: 16, height: 16, borderRadius: '50%', background: '#fff',
    position: 'absolute' as any, top: 3, left: on ? 21 : 3,
    transition: 'left 0.2s',
  }),
  progressBar: (pct: number) => ({
    height: 6, borderRadius: 3, background: T.border, overflow: 'hidden' as any,
    width: '100%', marginTop: 6,
  }),
  progressFill: (pct: number) => ({
    height: '100%', borderRadius: 3, width: `${pct}%`,
    background: pct >= 80 ? T.success : pct >= 50 ? T.warning : T.danger,
    transition: 'width 0.3s',
  }),
  table: { width: '100%', borderCollapse: 'collapse' as any, fontSize: 13 } as any,
  th: { padding: '10px 12px', textAlign: 'left' as any, borderBottom: `1px solid ${T.border}`, color: T.textMuted, fontWeight: 600, fontSize: 12 } as any,
  td: { padding: '8px 12px', borderBottom: `1px solid ${T.border}`, color: T.text } as any,
  tdEmpty: { padding: '8px 12px', borderBottom: `1px solid ${T.border}`, color: T.danger, background: 'rgba(239,68,68,0.08)' } as any,
};

// ── Tipos ──
interface DbLanguage {
  code: string;
  name: string;
  native_name: string;
  flag: string;
  region: string;
  rtl: number;
  enabled: number;
  is_featured: number;
  translation_progress: number;
}

const NAMESPACES = ['common', 'auth', 'chat', 'agents', 'billing', 'admin', 'errors'];
const REGIONS = ['Americas', 'Europe', 'Asia', 'Middle East', 'Africa', 'Oceania'];

// ══════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ══════════════════════════════════════════
export function AdminI18n() {
  const [tab, setTab] = useState(0);
  const tabs = ['🌐 Idiomas', '✏️ Traduções', '🤖 Auto-Tradução', '📖 Glossário', '🚩 Feature Flags', '📊 Analytics', '⚙️ Configurações'];

  return (
    <div style={s.page}>
      <h1 style={s.title}>🌍 Internacionalização</h1>
      <p style={s.subtitle}>Gerencie idiomas, traduções e configurações de i18n</p>

      <div style={s.tabBar}>
        {tabs.map((t, i) => (
          <button key={i} style={s.tab(tab === i)} onClick={() => setTab(i)}>{t}</button>
        ))}
      </div>

      {tab === 0 && <TabIdiomas />}
      {tab === 1 && <TabTraducoes />}
      {tab === 2 && <TabAutoTraduzir />}
      {tab === 3 && <TabGlossario />}
      {tab === 4 && <TabFeatureFlags />}
      {tab === 5 && <TabAnalytics />}
      {tab === 6 && <TabConfiguracoes />}
    </div>
  );
}

// ══════════════════════════════════════════
// TAB 1: IDIOMAS (Grid)
// ══════════════════════════════════════════
function TabIdiomas() {
  const [dbLangs, setDbLangs] = useState<DbLanguage[]>([]);
  const [search, setSearch] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState<string | null>(null);

  const fetchLangs = useCallback(async () => {
    try {
      const res = await api.get<{ languages: DbLanguage[] }>('/admin/i18n/languages');
      setDbLangs(res.languages || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchLangs(); }, []);

  // Mesclar languages.ts com dados do D1
  const merged = useMemo(() => {
    const dbMap = new Map(dbLangs.map(l => [l.code, l]));
    return SUPPORTED_LANGUAGES.map(lang => ({
      ...lang,
      enabled: dbMap.get(lang.code)?.enabled === 1,
      isFeatured: dbMap.get(lang.code)?.is_featured === 1,
      progress: dbMap.get(lang.code)?.translation_progress ?? 0,
      inDb: dbMap.has(lang.code),
    }));
  }, [dbLangs]);

  const filtered = useMemo(() => {
    let list = merged;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(l =>
        l.name.toLowerCase().includes(q) ||
        l.nativeName.toLowerCase().includes(q) ||
        l.code.toLowerCase().includes(q)
      );
    }
    if (regionFilter) {
      list = list.filter(l => l.region === regionFilter);
    }
    // Ativos primeiro, depois por progresso
    list.sort((a, b) => {
      if (a.enabled !== b.enabled) return a.enabled ? -1 : 1;
      return b.progress - a.progress;
    });
    return list;
  }, [merged, search, regionFilter]);

  const toggleLang = async (lang: Language & { enabled: boolean; inDb: boolean }) => {
    setToggling(lang.code);
    try {
      if (!lang.inDb && !lang.enabled) {
        // Ativar: criar no D1
        await api.post('/admin/i18n/languages', {
          code: lang.code, name: lang.name, nativeName: lang.nativeName,
          flag: lang.flag, region: lang.region, rtl: lang.rtl || false,
        });
      } else {
        // Toggle enable/disable
        await api.put(`/admin/i18n/languages/${lang.code}`, { enabled: !lang.enabled });
      }
      await fetchLangs();
    } catch { /* ignore */ }
    setToggling(null);
  };

  const activeCount = merged.filter(l => l.enabled).length;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ fontSize: 14, color: T.textMuted }}>
          {activeCount} idiomas ativos de {SUPPORTED_LANGUAGES.length} disponíveis
        </span>
      </div>

      <div style={s.searchRow}>
        <input
          style={s.input}
          placeholder="🔍 Buscar idioma..."
          value={search}
          onInput={(e: any) => setSearch(e.target.value)}
        />
        <select style={s.select} value={regionFilter} onChange={(e: any) => setRegionFilter(e.target.value)}>
          <option value="">Todas as regiões</option>
          {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: T.textMuted }}>Carregando...</div>
      ) : (
        <div style={s.grid}>
          {filtered.map(lang => (
            <div key={lang.code} style={s.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div>
                  <span style={{ fontSize: 24 }}>{lang.flag}</span>
                  <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginTop: 4 }}>{lang.nativeName}</div>
                  <div style={{ fontSize: 12, color: T.textMuted }}>{lang.code}</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                  <button
                    style={s.toggle(lang.enabled)}
                    onClick={() => toggleLang(lang)}
                    disabled={toggling === lang.code}
                  >
                    <div style={s.toggleDot(lang.enabled)} />
                  </button>
                  {lang.enabled && <span style={s.badge(lang.progress)}>{lang.progress}%</span>}
                  {lang.enabled && (
                    <button
                      style={{
                        background: lang.isFeatured ? T.primary : 'rgba(255,255,255,0.08)',
                        border: 'none', borderRadius: 6, padding: '3px 8px',
                        fontSize: 11, fontWeight: 600, cursor: 'pointer',
                        color: lang.isFeatured ? '#fff' : T.textMuted,
                        transition: 'all 0.15s',
                      }}
                      title={lang.isFeatured ? 'Remover dos principais' : 'Marcar como principal (aparece no menu do perfil)'}
                      onClick={async () => {
                        try {
                          await api.put(`/admin/i18n/languages/${lang.code}`, { is_featured: !lang.isFeatured });
                          await fetchLangs();
                        } catch { /* ignore */ }
                      }}
                    >
                      {lang.isFeatured ? '⭐ Principal' : '☆ Principal'}
                    </button>
                  )}
                </div>
              </div>
              {lang.enabled && (
                <div style={s.progressBar(lang.progress)}>
                  <div style={s.progressFill(lang.progress)} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════
// TAB 2: TRADUÇÕES (Editor)
// ══════════════════════════════════════════
function TabTraducoes() {
  const [dbLangs, setDbLangs] = useState<DbLanguage[]>([]);
  const [selectedLang, setSelectedLang] = useState('en-US');
  const [selectedNs, setSelectedNs] = useState('common');
  const [sourceData, setSourceData] = useState<Record<string, string>>({});
  const [targetData, setTargetData] = useState<Record<string, string>>({});
  const [editedData, setEditedData] = useState<Record<string, string>>({});
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ languages: DbLanguage[] }>('/admin/i18n/languages')
      .then(r => setDbLangs((r.languages || []).filter(l => l.enabled)));
  }, []);

  const loadTranslations = useCallback(async () => {
    setLoading(true);
    try {
      // Carregar source (pt-BR)
      const src = await api.get<{ translations: Record<string, Record<string, string>> }>(
        `/admin/i18n/translations/pt-BR?namespace=${selectedNs}`
      );
      setSourceData(src.translations?.[selectedNs] || {});

      // Carregar target
      const tgt = await api.get<{ translations: Record<string, Record<string, string>> }>(
        `/admin/i18n/translations/${selectedLang}?namespace=${selectedNs}`
      );
      const td = tgt.translations?.[selectedNs] || {};
      setTargetData(td);
      setEditedData({ ...td });
    } catch { /* ignore */ }
    setLoading(false);
  }, [selectedLang, selectedNs]);

  useEffect(() => { loadTranslations(); }, [selectedLang, selectedNs]);

  const keys = useMemo(() => {
    const allKeys = Object.keys(sourceData);
    if (!search) return allKeys;
    const q = search.toLowerCase();
    return allKeys.filter(k =>
      k.toLowerCase().includes(q) ||
      sourceData[k]?.toLowerCase().includes(q) ||
      editedData[k]?.toLowerCase().includes(q)
    );
  }, [sourceData, editedData, search]);

  const translatedCount = keys.filter(k => editedData[k]?.trim()).length;
  const totalCount = Object.keys(sourceData).length;
  const pct = totalCount > 0 ? Math.round((translatedCount / totalCount) * 100) : 0;

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/i18n/translations/${selectedLang}`, {
        namespace: selectedNs,
        translations: editedData,
      });
    } catch { /* ignore */ }
    setSaving(false);
  };

  const hasChanges = JSON.stringify(editedData) !== JSON.stringify(targetData);

  return (
    <div>
      <div style={s.searchRow}>
        <select style={s.select} value={selectedLang} onChange={(e: any) => setSelectedLang(e.target.value)}>
          {dbLangs.map(l => (
            <option key={l.code} value={l.code}>{l.flag} {l.native_name} ({l.code})</option>
          ))}
        </select>
        <select style={s.select} value={selectedNs} onChange={(e: any) => setSelectedNs(e.target.value)}>
          {NAMESPACES.map(ns => <option key={ns} value={ns}>{ns}</option>)}
        </select>
        <input
          style={s.input}
          placeholder="🔍 Buscar por key ou texto..."
          value={search}
          onInput={(e: any) => setSearch(e.target.value)}
        />
        <button
          style={s.btn(hasChanges ? 'primary' : 'ghost')}
          onClick={handleSave}
          disabled={saving || !hasChanges}
        >
          {saving ? '⏳ Salvando...' : '💾 Salvar'}
        </button>
      </div>

      {/* Barra de progresso */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: T.textMuted, marginBottom: 4 }}>
          <span>{translatedCount} de {totalCount} keys traduzidas</span>
          <span style={s.badge(pct)}>{pct}%</span>
        </div>
        <div style={s.progressBar(pct)}>
          <div style={s.progressFill(pct)} />
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: T.textMuted }}>Carregando traduções...</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={{ ...s.th, width: '25%' }}>Key</th>
                <th style={{ ...s.th, width: '37%' }}>pt-BR (fonte)</th>
                <th style={{ ...s.th, width: '38%' }}>{selectedLang}</th>
              </tr>
            </thead>
            <tbody>
              {keys.map(key => {
                const isEmpty = !editedData[key]?.trim();
                const isEditing = editingKey === key;
                return (
                  <tr key={key}>
                    <td style={{ ...s.td, fontFamily: 'monospace', fontSize: 12, color: T.textMuted }}>{key}</td>
                    <td style={s.td}>{sourceData[key]}</td>
                    <td
                      style={isEmpty ? s.tdEmpty : s.td}
                      onClick={() => setEditingKey(key)}
                    >
                      {isEditing ? (
                        <input
                          style={{ ...s.input, padding: '4px 8px', fontSize: 13, width: '100%' }}
                          value={editedData[key] || ''}
                          onInput={(e: any) => setEditedData(prev => ({ ...prev, [key]: e.target.value }))}
                          onBlur={() => setEditingKey(null)}
                          onKeyDown={(e: any) => { if (e.key === 'Enter') setEditingKey(null); }}
                          autoFocus
                        />
                      ) : (
                        <span style={{ cursor: 'pointer' }}>
                          {editedData[key] || '—'}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════
// TAB 3: AUTO-TRADUÇÃO
// ══════════════════════════════════════════
function TabAutoTraduzir() {
  const [dbLangs, setDbLangs] = useState<DbLanguage[]>([]);
  const [targetLang, setTargetLang] = useState('');
  const [selectedNs, setSelectedNs] = useState<string[]>([...NAMESPACES]);
  const [translating, setTranslating] = useState(false);
  const [progress, setProgress] = useState('');
  const [results, setResults] = useState<Record<string, Record<string, string>> | null>(null);
  const [saving, setSaving] = useState(false);
  const [coverage, setCoverage] = useState<Record<string, { source: number; translated: number }>>({});
  const [nsProgress, setNsProgress] = useState<string>('');

  useEffect(() => {
    api.get<{ languages: DbLanguage[] }>('/admin/i18n/languages')
      .then(r => {
        const langs = (r.languages || []).filter(l => l.enabled && l.code !== 'pt-BR');
        setDbLangs(langs);
        if (langs.length > 0 && !targetLang) setTargetLang(langs[0].code);
      });
  }, []);

  const toggleNs = (ns: string) => {
    setSelectedNs(prev =>
      prev.includes(ns) ? prev.filter(n => n !== ns) : [...prev, ns]
    );
  };

  const handleTranslate = async () => {
    if (!targetLang || selectedNs.length === 0) return;
    setTranslating(true);
    setProgress(`Traduzindo ${selectedNs.length} namespace(s) pra ${targetLang}...`);
    setResults(null);

    try {
      const res = await api.post<{
        translations: Record<string, Record<string, string>>;
        message: string;
      }>('/admin/i18n/auto-translate', {
        targetLang,
        namespaces: selectedNs,
      }, { timeout: 120000 });

      const trans = res.translations || {};
      setResults(trans);

      // Calcular cobertura por namespace
      const cov: Record<string, { source: number; translated: number }> = {};
      for (const [ns, data] of Object.entries(trans)) {
        const translatedKeys = Object.keys(data).filter(k => data[k]?.trim());
        cov[ns] = { source: Object.keys(data).length, translated: translatedKeys.length };
      }
      setCoverage(cov);

      const totalSrc = Object.values(cov).reduce((a, c) => a + c.source, 0);
      const totalTrans = Object.values(cov).reduce((a, c) => a + c.translated, 0);
      const pct = totalSrc > 0 ? Math.round((totalTrans / totalSrc) * 100) : 0;
      setProgress(`✅ Tradução concluída! ${totalTrans}/${totalSrc} strings (${pct}% cobertura). Revise e salve.`);
    } catch (e: any) {
      setProgress(`❌ Erro: ${e?.message || 'Falha na tradução'}`);
    }
    setTranslating(false);
  };

  const handleSaveAll = async () => {
    if (!results) return;
    setSaving(true);
    try {
      for (const [ns, translations] of Object.entries(results)) {
        await api.put(`/admin/i18n/translations/${targetLang}`, {
          namespace: ns,
          translations,
        });
      }
      setProgress('✅ Todas as traduções salvas com sucesso!');
      setResults(null);
    } catch (e: any) {
      setProgress(`❌ Erro ao salvar: ${e?.message}`);
    }
    setSaving(false);
  };

  return (
    <div>
      <div style={{ ...s.card, marginBottom: 16 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 12 }}>🤖 Tradução Automática via IA</h3>
        <p style={{ fontSize: 13, color: T.textMuted, marginBottom: 16 }}>
          Usa o Elon Router pra traduzir automaticamente do pt-BR pro idioma selecionado.
        </p>

        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          <select style={s.select} value={targetLang} onChange={(e: any) => setTargetLang(e.target.value)}>
            {dbLangs.map(l => (
              <option key={l.code} value={l.code}>{l.flag} {l.native_name}</option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 8 }}>Namespaces:</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {NAMESPACES.map(ns => (
              <label key={ns} style={{
                display: 'flex', alignItems: 'center', gap: 6, fontSize: 13,
                padding: '6px 12px', borderRadius: 8, cursor: 'pointer',
                background: selectedNs.includes(ns) ? T.primaryBgSubtle : T.bgInput,
                border: `1px solid ${selectedNs.includes(ns) ? T.primary : T.border}`,
                color: selectedNs.includes(ns) ? T.primary : T.text,
              }}>
                <input
                  type="checkbox"
                  checked={selectedNs.includes(ns)}
                  onChange={() => toggleNs(ns)}
                  style={{ display: 'none' }}
                />
                {selectedNs.includes(ns) ? '✅' : '⬜'} {ns}
              </label>
            ))}
          </div>
        </div>

        <button
          style={s.btn('primary')}
          onClick={handleTranslate}
          disabled={translating || !targetLang || selectedNs.length === 0}
        >
          {translating ? '⏳ Traduzindo...' : '🚀 Traduzir com IA'}
        </button>

        {progress && (
          <div style={{ marginTop: 12, fontSize: 13, color: T.textSecondary, padding: '8px 12px', background: T.bgInput, borderRadius: 8 }}>
            {progress}
          </div>
        )}
      </div>

      {/* Preview dos resultados */}
      {results && (
        <div style={{ ...s.card }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: T.text }}>📋 Preview da Tradução</h3>
            <button
              style={s.btn('success')}
              onClick={handleSaveAll}
              disabled={saving}
            >
              {saving ? '⏳ Salvando...' : '✅ Aprovar e Salvar Tudo'}
            </button>
          </div>

          {Object.entries(results).map(([ns, data]) => {
            const cov = coverage[ns];
            const pct = cov && cov.source > 0 ? Math.round((cov.translated / cov.source) * 100) : 0;
            return (
            <div key={ns} style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <h4 style={{ fontSize: 14, fontWeight: 600, color: T.primary, margin: 0 }}>{ns}</h4>
                {cov && <span style={s.badge(pct)}>{pct}% ({cov.translated}/{cov.source})</span>}
              </div>
              <div style={s.progressBar(pct)}><div style={s.progressFill(pct)} /></div>
              <div style={{ maxHeight: 300, overflowY: 'auto', background: T.bg, borderRadius: 8, padding: 12 }}>
                <pre style={{ fontSize: 12, color: T.text, margin: 0, whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(data, null, 2)}
                </pre>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════
// TAB 4: CONFIGURAÇÕES
// ══════════════════════════════════════════
function TabConfiguracoes() {
  const [dbLangs, setDbLangs] = useState<DbLanguage[]>([]);
  const [defaultLang, setDefaultLang] = useState('en-US');
  const [fallbackLang, setFallbackLang] = useState('en-US');
  const [settingsSaved, setSettingsSaved] = useState<string | null>(null);
  const [detection, setDetection] = useState({ ip: true, browser: true, cookie: true });

  // Idiomas com tradução completa disponível (inline no bundle)
  const TRANSLATED_LANGS = ['pt-BR', 'en-US', 'es-ES', 'fr-FR', 'de-DE'];
  const LANG_LABELS: Record<string, string> = {
    'pt-BR': '🇧🇷 Português (Brasil)',
    'en-US': '🇺🇸 English (US)',
    'es-ES': '🇪🇸 Español',
    'fr-FR': '🇫🇷 Français',
    'de-DE': '🇩🇪 Deutsch',
  };

  useEffect(() => {
    api.get<{ languages: DbLanguage[] }>('/admin/i18n/languages')
      .then(r => setDbLangs((r.languages || []).filter(l => l.enabled)));
    // Buscar configurações de idioma
    api.get<any>('/admin/i18n/settings')
      .then(r => {
        if (r?.default_locale) setDefaultLang(r.default_locale);
        if (r?.fallback_locale) setFallbackLang(r.fallback_locale);
      })
      .catch(() => {});
  }, []);

  const saveSetting = async (key: 'default_locale' | 'fallback_locale', value: string) => {
    if (key === 'default_locale') setDefaultLang(value);
    else setFallbackLang(value);
    try {
      await api.put('/admin/i18n/settings', { [key]: value });
      setSettingsSaved(key);
      setTimeout(() => setSettingsSaved(null), 2000);
    } catch { /* silencioso */ }
  };

  const activeLangs = dbLangs.filter(l => l.enabled);
  const rtlLangs = dbLangs.filter(l => l.rtl);

  return (
    <div>
      <div style={{ ...s.card, marginBottom: 16 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 16 }}>⚙️ Configurações de i18n</h3>

        {/* Idioma padrão da página inicial */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: T.text, display: 'block', marginBottom: 6 }}>
            🏠 Idioma Padrão (Landing Page)
          </label>
          <p style={{ fontSize: 12, color: T.textSecondary, margin: '0 0 8px' }}>
            Idioma exibido na landing page para visitantes novos (sem cookie). Também é o idioma da rota <code>/</code>.
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <select style={s.select} value={defaultLang} onChange={(e: any) => saveSetting('default_locale', e.target.value)}>
              {TRANSLATED_LANGS.map(code => (
                <option key={code} value={code}>{LANG_LABELS[code] || code}</option>
              ))}
            </select>
            {settingsSaved === 'default_locale' && <span style={{ fontSize: 12, color: T.success }}>✓ Salvo!</span>}
          </div>
        </div>

        {/* Idioma de fallback */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: T.text, display: 'block', marginBottom: 6 }}>
            🔄 Idioma de Fallback
          </label>
          <p style={{ fontSize: 12, color: T.textSecondary, margin: '0 0 8px' }}>
            Quando uma tradução não existe no idioma do usuário, exibe neste idioma. Funciona como "segunda opção" global.
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <select style={s.select} value={fallbackLang} onChange={(e: any) => saveSetting('fallback_locale', e.target.value)}>
              {TRANSLATED_LANGS.map(code => (
                <option key={code} value={code}>{LANG_LABELS[code] || code}</option>
              ))}
            </select>
            {settingsSaved === 'fallback_locale' && <span style={{ fontSize: 12, color: T.success }}>✓ Salvo!</span>}
          </div>
        </div>

        {/* Detecção automática */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: T.text, display: 'block', marginBottom: 10 }}>
            Detecção Automática de Idioma
          </label>
          {[
            { key: 'browser' as const, label: '🌐 Navegador (Accept-Language)' },
            { key: 'cookie' as const, label: '🍪 Cookie (preferência salva)' },
            { key: 'ip' as const, label: '📍 IP (geolocalização)' },
          ].map(opt => (
            <div key={opt.key} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <button
                style={s.toggle(detection[opt.key])}
                onClick={() => setDetection(prev => ({ ...prev, [opt.key]: !prev[opt.key] }))}
              >
                <div style={s.toggleDot(detection[opt.key])} />
              </button>
              <span style={{ fontSize: 13, color: T.text }}>{opt.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Status */}
      <div style={{ ...s.card }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 16 }}>📊 Status</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
          <StatCard label="Idiomas Ativos" value={String(activeLangs.length)} icon="🌐" />
          <StatCard label="Total Disponível" value={String(SUPPORTED_LANGUAGES.length)} icon="📚" />
          <StatCard label="Idiomas RTL" value={String(rtlLangs.length)} icon="↗️" />
          <StatCard label="Namespaces" value={String(NAMESPACES.length)} icon="📁" />
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════
// TAB: GLOSSÁRIO
// ══════════════════════════════════════════
function TabGlossario() {
  const [terms, setTerms] = useState<any[]>([]);
  const [newTerm, setNewTerm] = useState({ term: '', translation: '', context: '', never_translate: false });
  const [editId, setEditId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadTerms(); }, []);

  async function loadTerms() {
    try {
      const res = await api.get('/admin/i18n/glossary');
      setTerms((res.data as any)?.glossary || []);
    } catch { setTerms([]); }
    setLoading(false);
  }

  async function addTerm() {
    if (!newTerm.term || !newTerm.translation) return;
    try {
      await api.post('/admin/i18n/glossary', newTerm);
      setNewTerm({ term: '', translation: '', context: '', never_translate: false });
      await loadTerms();
    } catch { /* silencioso */ }
  }

  async function deleteTerm(id: number) {
    try {
      await api.delete(`/admin/i18n/glossary/${id}`);
      await loadTerms();
    } catch { /* silencioso */ }
  }

  async function updateTerm(id: number, data: any) {
    try {
      await api.put(`/admin/i18n/glossary/${id}`, data);
      setEditId(null);
      await loadTerms();
    } catch { /* silencioso */ }
  }

  return (
    <div>
      {/* Formulário de adição */}
      <div style={{ ...s.card, marginBottom: 16 }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 12 }}>➕ Novo Termo</h3>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as any }}>
          <input style={{ ...s.input, flex: 1, minWidth: 150 }} placeholder="Termo (ex: agente)"
            value={newTerm.term} onInput={(e: any) => setNewTerm({ ...newTerm, term: e.target.value })} />
          <input style={{ ...s.input, flex: 1, minWidth: 150 }} placeholder="Tradução (ex: agent)"
            value={newTerm.translation} onInput={(e: any) => setNewTerm({ ...newTerm, translation: e.target.value })} />
          <input style={{ ...s.input, flex: 1, minWidth: 150 }} placeholder="Contexto (opcional)"
            value={newTerm.context} onInput={(e: any) => setNewTerm({ ...newTerm, context: e.target.value })} />
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: T.textMuted }}>
            <input type="checkbox" checked={newTerm.never_translate}
              onChange={(e: any) => setNewTerm({ ...newTerm, never_translate: e.target.checked })} />
            Nunca traduzir
          </label>
          <button style={s.btn('primary')} onClick={addTerm}>Adicionar</button>
        </div>
      </div>

      {/* Lista de termos */}
      <div style={s.card}>
        <h3 style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 12 }}>
          📖 Glossário ({terms.length} termos)
        </h3>
        {loading ? <p style={{ color: T.textMuted }}>Carregando...</p> : (
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Termo</th>
                <th style={s.th}>Tradução</th>
                <th style={s.th}>Contexto</th>
                <th style={s.th}>🚫</th>
                <th style={s.th}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {terms.map((t: any) => (
                <tr key={t.id}>
                  <td style={s.td}><strong>{t.term}</strong></td>
                  <td style={s.td}>{t.translation}</td>
                  <td style={s.td}><span style={{ color: T.textMuted }}>{t.context || '—'}</span></td>
                  <td style={s.td}>{t.never_translate ? '🚫' : ''}</td>
                  <td style={s.td}>
                    <button style={s.btn('danger')} onClick={() => deleteTerm(t.id)}>🗑</button>
                  </td>
                </tr>
              ))}
              {terms.length === 0 && (
                <tr><td style={{ ...s.td, textAlign: 'center', color: T.textMuted }} colSpan={5}>Nenhum termo cadastrado</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════
// TAB: FEATURE FLAGS
// ══════════════════════════════════════════
function TabFeatureFlags() {
  const [flags, setFlags] = useState<any[]>([]);
  const [newKey, setNewKey] = useState('');
  const [loading, setLoading] = useState(true);

  const LOCALES = ['pt-BR', 'en-US', 'es-ES', 'fr-FR', 'de-DE'];
  const DEFAULT_FEATURES = ['pix_payment', 'boleto_payment', 'stripe_payment', 'whatsapp_support', 'custom_domain'];

  useEffect(() => { loadFlags(); }, []);

  async function loadFlags() {
    try {
      const res = await api.get('/admin/i18n/feature-flags');
      setFlags((res.data as any)?.flags || []);
    } catch { setFlags([]); }
    setLoading(false);
  }

  // Agrupar flags por feature_key
  const featureKeys = [...new Set([
    ...DEFAULT_FEATURES,
    ...flags.map((f: any) => f.feature_key),
    ...(newKey ? [newKey] : []),
  ])];

  function isEnabled(featureKey: string, locale: string): boolean {
    return flags.some((f: any) => f.feature_key === featureKey && f.locale === locale && f.enabled === 1);
  }

  async function toggleFlag(featureKey: string, locale: string) {
    const current = isEnabled(featureKey, locale);
    try {
      await api.put('/admin/i18n/feature-flags', {
        feature_key: featureKey,
        locale,
        enabled: !current,
      });
      await loadFlags();
    } catch { /* silencioso */ }
  }

  return (
    <div>
      <div style={{ ...s.card, marginBottom: 16 }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 12 }}>🚩 Feature Flags por Locale</h3>
        <p style={{ fontSize: 13, color: T.textMuted, marginBottom: 12 }}>
          Habilite/desabilite features por idioma. Ex: PIX só pra pt-BR, Stripe pra en-US.
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <input style={{ ...s.input, maxWidth: 300 }} placeholder="Nova feature key..."
            value={newKey} onInput={(e: any) => setNewKey(e.target.value)} />
        </div>
      </div>

      <div style={s.card}>
        {loading ? <p style={{ color: T.textMuted }}>Carregando...</p> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Feature</th>
                  {LOCALES.map(l => <th key={l} style={{ ...s.th, textAlign: 'center' }}>{l}</th>)}
                </tr>
              </thead>
              <tbody>
                {featureKeys.map(fk => (
                  <tr key={fk}>
                    <td style={s.td}><code>{fk}</code></td>
                    {LOCALES.map(locale => (
                      <td key={locale} style={{ ...s.td, textAlign: 'center' }}>
                        <button
                          style={s.toggle(isEnabled(fk, locale))}
                          onClick={() => toggleFlag(fk, locale)}
                        >
                          <div style={s.toggleDot(isEnabled(fk, locale))} />
                        </button>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════
// TAB: ANALYTICS
// ══════════════════════════════════════════
function TabAnalytics() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadAnalytics(); }, []);

  async function loadAnalytics() {
    try {
      const res = await api.get('/admin/i18n/analytics?days=30');
      setData((res.data as any) || {});
    } catch { setData({}); }
    setLoading(false);
  }

  if (loading) return <p style={{ color: T.textMuted }}>Carregando analytics...</p>;

  const missingKeys = data?.missingKeys || [];
  const localeUsage = data?.localeUsage || [];
  const fallbacks = data?.fallbacks || [];

  // Valor máximo para barras proporcionais
  const maxUsage = Math.max(1, ...localeUsage.map((l: any) => l.total));

  return (
    <div>
      {/* Uso por idioma */}
      <div style={{ ...s.card, marginBottom: 16 }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 12 }}>🌐 Uso por Idioma (30 dias)</h3>
        {localeUsage.length === 0 ? (
          <p style={{ color: T.textMuted, fontSize: 13 }}>Nenhum dado ainda. Os dados aparecem conforme os usuários acessam o site.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {localeUsage.map((l: any) => (
              <div key={l.locale} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ width: 60, fontSize: 13, fontWeight: 600, color: T.text }}>{l.locale}</span>
                <div style={{ flex: 1, height: 20, background: T.border, borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', borderRadius: 4, background: T.primary,
                    width: `${(l.total / maxUsage) * 100}%`, transition: 'width 0.3s',
                  }} />
                </div>
                <span style={{ fontSize: 12, color: T.textMuted, width: 60, textAlign: 'right' }}>{l.total}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Missing Keys */}
      <div style={{ ...s.card, marginBottom: 16 }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 12 }}>
          ⚠️ Missing Keys ({missingKeys.length})
        </h3>
        {missingKeys.length === 0 ? (
          <p style={{ color: T.textMuted, fontSize: 13 }}>Nenhuma key faltando nos últimos 30 dias. 🎉</p>
        ) : (
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Key</th>
                <th style={s.th}>Namespace</th>
                <th style={s.th}>Locale</th>
                <th style={s.th}>Ocorrências</th>
              </tr>
            </thead>
            <tbody>
              {missingKeys.slice(0, 20).map((mk: any, i: number) => (
                <tr key={i}>
                  <td style={s.tdEmpty}><code>{mk.key}</code></td>
                  <td style={s.td}>{mk.namespace}</td>
                  <td style={s.td}>{mk.locale}</td>
                  <td style={s.td}>{mk.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Fallbacks por dia */}
      <div style={s.card}>
        <h3 style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 12 }}>
          🔄 Fallbacks por Dia
        </h3>
        {fallbacks.length === 0 ? (
          <p style={{ color: T.textMuted, fontSize: 13 }}>Nenhum fallback registrado.</p>
        ) : (
          <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', height: 100 }}>
            {fallbacks.slice(0, 30).reverse().map((f: any) => {
              const maxFb = Math.max(1, ...fallbacks.map((x: any) => x.total));
              const height = Math.max(4, (f.total / maxFb) * 100);
              return (
                <div key={f.date} title={`${f.date}: ${f.total}`}
                  style={{
                    flex: 1, minWidth: 8, maxWidth: 20, height: `${height}%`,
                    background: T.warning, borderRadius: '2px 2px 0 0',
                  }} />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon }: { label: string; value: string; icon: string }) {
  return (
    <div style={{ background: T.bgInput, borderRadius: 10, padding: 16, textAlign: 'center' }}>
      <div style={{ fontSize: 24 }}>{icon}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: T.text }}>{value}</div>
      <div style={{ fontSize: 12, color: T.textMuted }}>{label}</div>
    </div>
  );
}

export default AdminI18n;
